import moment from 'moment';

// Timestamp formatters for table cells (FNP-924).
//
// ═══ A NULL TIMESTAMP MUST NOT RENDER AS A REAL ONE ═══
//
// `moment(undefined)` returns THE CURRENT DATE AND TIME. An unguarded
// formatter therefore renders a plausible timestamp of *right now* for a row
// whose value is null — fabricated data a user cannot distinguish from a
// genuine reading. That is strictly worse than blank: blank is visibly
// missing; "Aug 14, 2026 19:50" reads as fact.
//
// Nullable timestamps are normal here — `created`/`updated` are AWSDateTime
// (nullable) on most entities, so this path is reached with real data.
//
// MODULE_FEATURE_CHECKLIST §13: "Nullable fields render a placeholder (—),
// never blank or `undefined`."

/** What an absent or unparseable timestamp renders as. */
export const TIMESTAMP_PLACEHOLDER = '—';

/**
 * Guards the three absent-data cases that actually reach these formatters:
 * null/undefined, '' (how an absent AWSDateTime often arrives), and a value
 * moment cannot parse — garbage in preference to a fabricated date out.
 *
 * NOTE epoch 0 is a REAL instant and must survive: a falsy check would hide it.
 */
const isRenderable = (ts?: unknown): ts is string | number => {
    if (ts === null || typeof ts === 'undefined') {
        return false;
    }
    // Only primitives are timestamps. A ReactElement or object reaching here is
    // absent/unsupported data, not something to coerce — moment would happily
    // turn many objects into a plausible date.
    if (typeof ts !== 'string' && typeof ts !== 'number') {
        return false;
    }
    if (typeof ts === 'string' && ts.trim() === '') {
        return false;
    }
    return moment(ts).isValid();
};

export const formatTimestamp = (ts?: unknown, format = 'MMM DD, YYYY HH:mm:ss') => (
    <>
        {isRenderable(ts)
            ? moment(ts).format(format)
            : TIMESTAMP_PLACEHOLDER}
    </>
);
